import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { OrgProvider } from "@/lib/orgContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import NewAudit from "@/pages/NewAudit";
import AuditReport from "@/pages/AuditReport";
import Recommendations from "@/pages/Recommendations";
import FixGenerator from "@/pages/FixGenerator";
import ContentStrategist from "@/pages/ContentStrategist";
import Competitors from "@/pages/Competitors";
import Monitoring from "@/pages/Monitoring";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Integrations from "@/pages/Integrations";
import AdminAnalytics from "@/pages/AdminAnalytics";
import GoogleCallback from "@/pages/GoogleCallback";
import PublicScore from "@/pages/PublicScore";
import About from "@/pages/legal/About";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Refund from "@/pages/legal/Refund";
import Faq from "@/pages/legal/Faq";
import Organizations from "@/pages/Organizations";
import OrgMembers from "@/pages/OrgMembers";
import Projects from "@/pages/Projects";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-sm uppercase tracking-widest">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-sm uppercase tracking-widest">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <OrgProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/audits/new" element={<Protected><NewAudit /></Protected>} />
            <Route path="/audits/:id" element={<Protected><AuditReport /></Protected>} />
            <Route path="/audits/:id/recommendations" element={<Protected><Recommendations /></Protected>} />
            <Route path="/fixes" element={<Protected><FixGenerator /></Protected>} />
            <Route path="/content" element={<Protected><ContentStrategist /></Protected>} />
            <Route path="/competitors" element={<Protected><Competitors /></Protected>} />
            <Route path="/monitoring" element={<Protected><Monitoring /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/integrations" element={<AdminOnly><Integrations /></AdminOnly>} />
            <Route path="/admin/analytics" element={<AdminOnly><AdminAnalytics /></AdminOnly>} />
            <Route path="/organizations" element={<Protected><Organizations /></Protected>} />
            <Route path="/organizations/:orgId/members" element={<Protected><OrgMembers /></Protected>} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/s/:slug" element={<PublicScore />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </OrgProvider>
    </AuthProvider>
  );
}

export default App;
